/**
 * S3 Service
 * Handles all AWS S3 operations using manifest.json
 */

import { S3_CONFIG } from '../config/s3Config';
// import {SPECIAL_PARASHA_KEYS} from '../consts/constTora';
class S3Service {
  constructor() {
    this.baseUrl = `https://${S3_CONFIG.BUCKET}.s3.${S3_CONFIG.REGION}.amazonaws.com`;
    this.backendUrl = process.env.REACT_APP_BACKEND_URL?  process.env.REACT_APP_BACKEND_URL: 'http://localhost:3001';
    this.manifest = null;
    this.searchIndex = null;
  }

  async fetchManifest() {
  // 1️⃣ Check localStorage
  let storedManifest = null;
  try {
    storedManifest = JSON.parse(localStorage.getItem('manifest'));
  } catch (e) {
    localStorage.removeItem('manifest');
  }

  const localVersion = storedManifest?.version;

  // 2️⃣ Check version with Lambda
  let remoteVersion;
  try {
    const versionResp = await fetch(`${this.backendUrl}/manifest`);
    if (!versionResp.ok) throw new Error('Failed to fetch manifest version');
    const data = await versionResp.json();
    remoteVersion = data.version;
  } catch (e) {
    console.error('Error checking manifest version:', e);
    // fallback: use local cached manifest if available
    if (storedManifest) return storedManifest;
    throw e;
  }

  // 3️⃣ Compare versions
  if (storedManifest && localVersion === remoteVersion) {
    // Same version → return cached manifest
    return storedManifest;
  }

  // 4️⃣ Versions differ → fetch new manifest
  try {
    const url = `${this.baseUrl}/manifest.json`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch manifest: ${response.status}`);

    const manifest = await response.json();
    manifest.books.forEach(book => {
  book.parashot = book.parashot.filter(p => {
    return !p.parasha.startsWith(" ");
  });
});

    // Save to localStorage
    try {
      localStorage.setItem('manifest', JSON.stringify(manifest));
    } catch (e) {
      console.warn('Failed to save manifest to localStorage', e);
    }

    return manifest;
  } catch (error) {
    console.error('Error fetching new manifest:', error);
    throw error;
  }
}

  /**
   * Get books from manifest
   */
  getBooksFromManifest(manifest) {
    if (!manifest || !manifest.books) return [];
    
    return manifest.books.map(book => ({
      id: book.humash.toLowerCase(),
      name: this.getHebrewBookName(book.humash),
      key: book.humash,
      parshiot: book.parashot.map(parasha => ({
        name: parasha.parasha.trim(),
        key: parasha.parasha.trim(),
        files: parasha.files || []
      }))
    }));
  }

  /**
   * Convert English book name to Hebrew
   */
  getHebrewBookName(englishName) {
    const names = {
      'Bereshit': 'בראשית',
      'Shemot': 'שמות',
      'Vayikra': 'ויקרא',
      'Bamidbar': 'במדבר',
      'Devarim': 'דברים',
      'Holidays': 'חגים'
    };
    return names[englishName] || englishName;
  }

  /**
   * Fetch HTML content for a specific file
   * Path structure: books/Humash/Parasha/FileName.html
   */
  async fetchFileContent(bookKey, parshaKey, fileName) {
    // if (SPECIAL_PARASHA_KEYS.includes(parshaKey.trim())) {
    //   parshaKey = ` ${parshaKey}`;
    // }
    try {
      const url = `${this.baseUrl}/books/${bookKey}/${encodeURIComponent(parshaKey)}/${encodeURIComponent(fileName)}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      
      return await response.text();
    } catch (error) {
      console.error('Error fetching content:', error);
      throw error;
    }
  }

  /**
   * Get PDF URL for a file
   * Replace .html with .pdf in the filename
   */
  getPDFUrl(bookKey, parshaKey, fileName) {
    const pdfFileName = fileName.replace('.html', '.pdf');
    return `${this.baseUrl}/books/${bookKey}/${parshaKey}/${pdfFileName}`;
  }

  /**
   * Search across all content
   */
  async searchContent(query, page = 1, pageSize = 20, book) {
  try {
    if (!query.trim()) return [];

    // Build query params
    const params = new URLSearchParams({
      q: query.trim(),
      page: page.toString(),
      pageSize: pageSize.toString()
    });

    // Optional book filter
    if (book) params.append('book', book);

    // Call your search Lambda
    const url = `${this.backendUrl}/search?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error('Search Lambda error', response.status);
      return [];
    }

    const data = await response.json();

    // data.results contains the array of matches
    // data.totalResults contains the total number of matches
    return {
      results: data.results || [],
      totalResults: data.totalResults || 0,
      page: data.page || 1,
      pageSize: data.pageSize || pageSize,
      totalPages: data.totalPages || 1
    };
  } catch (error) {
    console.error('Search error:', error);
    return { results: [], totalResults: 0, page: 1, pageSize, totalPages: 1 };
  }
}

}

export default new S3Service();